<div class="detail-page">

  <!-- Page Header -->
  <div class="page-header">
    <button class="back-btn" (click)="goBack()">
      <i class="fas fa-arrow-left"></i> Back to Appointments
    </button>
    <h1 class="page-title">Appointment Details</h1>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="loading-state">
    <i class="fas fa-spinner fa-spin"></i>
    <p>Loading appointment...</p>
  </div>

  <!-- Error -->
  <div *ngIf="!loading && errorMessage" class="error-state">
    <i class="fas fa-exclamation-triangle"></i>
    <p>{{ errorMessage }}</p>
    <button class="btn-back" (click)="goBack()">Go Back</button>
  </div>

  <!-- Content -->
  <div *ngIf="!loading && !errorMessage && appointment" class="detail-content">

    <!-- Status Banner -->
    <div class="status-banner" [ngClass]="getStatusClass(appointment.status)">
      <span class="status-label">{{ appointment.status }}</span>
      <span *ngIf="appointment.parentId" class="rescheduled-tag">
        <i class="fas fa-redo-alt"></i> Created through Rescheduling
      </span>
    </div>

    <!-- Action Buttons -->
    <div class="action-bar">
      <button *ngIf="canConfirm()" class="btn-action btn-accept" (click)="openRemarkPopup('confirm')" [disabled]="processingAction">
        <i class="fas fa-check"></i> Accept
      </button>
      <button *ngIf="canReject()" class="btn-action btn-reject" (click)="openRemarkPopup('reject')" [disabled]="processingAction">
        <i class="fas fa-times"></i> Reject
      </button>
      <button *ngIf="canCancel()" class="btn-action btn-cancel-appt" (click)="openRemarkPopup('cancel')" [disabled]="processingAction">
        <i class="fas fa-ban"></i> Cancel
      </button>
      <button *ngIf="canReschedule()" class="btn-action btn-reschedule" (click)="openRescheduleModal()" [disabled]="processingAction || processingReschedule">
        <i class="fas fa-calendar-alt"></i> Reschedule
      </button>
      <button *ngIf="canAssignAgent()" class="btn-action btn-assign" (click)="openAssignAgentModal()" [disabled]="processingAction">
        <i class="fas fa-user-plus"></i> Assign Agent
      </button>
      <button *ngIf="canUpdateFees()" class="btn-action btn-fees" (click)="openUpdateFeesModal()" [disabled]="processingAction">
        <i class="fas fa-dollar-sign"></i> Update Fees
      </button>
    </div>



    <!-- Unified Details Card -->
    <div class="detail-card">

      <!-- Customer -->
      <div class="card-section">
        <div class="card-section-title"><i class="fas fa-user"></i> Customer</div>
        <div class="fields-grid">
          <div class="field-row"><span class="field-label">Name</span><span class="field-value">{{ appointment.customerName || 'N/A' }}</span></div>
          <div class="field-row"><span class="field-label">Phone</span><span class="field-value">{{ appointment.phoneNumber || 'N/A' }}</span></div>
          <div class="field-row" *ngIf="appointment.parentId">
            <span class="field-label">Parent Appt ID</span>
            <span class="field-value mono">{{ appointment.parentId }}</span>
          </div>
        </div>
      </div>

      <div class="card-divider"></div>

      <!-- Schedule -->
      <div class="card-section">
        <div class="card-section-title"><i class="fas fa-calendar-alt"></i> Schedule</div>
        <div class="fields-grid">
          <div class="field-row"><span class="field-label">Date</span><span class="field-value">{{ formatDate(appointment.date) }}</span></div>
          <div class="field-row"><span class="field-label">Time Slot</span><span class="field-value">{{ appointment.timeSlot || 'Not specified' }}</span></div>
          <div class="field-row" *ngIf="appointment.dailyOrder != null"><span class="field-label">Daily Order</span><span class="field-value">{{ appointment.dailyOrder }}</span></div>
        </div>
      </div>

      <div class="card-divider"></div>

      <!-- Location -->
      <div class="card-section">
        <div class="card-section-title"><i class="fas fa-map-marker-alt"></i> Location</div>
        <div class="fields-grid">
          <div class="field-row"><span class="field-label">Address</span><span class="field-value">{{ appointment.address || 'N/A' }}</span></div>
          <div class="field-row"><span class="field-label">Location</span><span class="field-value">{{ appointment.locationName || 'N/A' }}</span></div>
          <div class="field-row"><span class="field-label">Zone</span><span class="field-value">{{ appointment.geoZoneName || 'N/A' }}</span></div>
          <div class="field-row"><span class="field-label">Pin Code</span><span class="field-value">{{ appointment.pinCode || 'N/A' }}</span></div>
          <div class="field-row" *ngIf="appointment.location && appointment.location.latitude && appointment.location.longitude">
            <span class="field-label">Map</span>
            <span class="field-value">
              <a [href]="getGoogleMapsUrl()" target="_blank" class="map-link">
                <i class="fas fa-external-link-alt"></i> Google Maps
              </a>
            </span>
          </div>
        </div>
      </div>

      <div class="card-divider"></div>

      <!-- Agent -->
      <div class="card-section">
        <div class="card-section-title"><i class="fas fa-truck"></i> Agent</div>
        <div class="fields-grid">
          <div class="field-row"><span class="field-label">Name</span><span class="field-value">{{ appointment.agentName || 'Not assigned' }}</span></div>
        </div>
      </div>

      <ng-container *ngIf="appointment.collectionCharge != null || appointment.platformChargeInPercent != null">
        <div class="card-divider"></div>

        <!-- Charges -->
        <div class="card-section">
          <div class="card-section-title"><i class="fas fa-rupee-sign"></i> Charges</div>
          <div class="fields-grid">
            <div class="field-row" *ngIf="appointment.collectionCharge != null">
              <span class="field-label">Collection Charge</span>
              <span class="field-value">
                {{ appointment.collectionCharge | currency:'INR':'symbol':'1.2-2' }}
                <span *ngIf="appointment.collectionChargeSource" class="source-tag">{{ appointment.collectionChargeSource }}</span>
              </span>
            </div>
            <div class="field-row" *ngIf="appointment.platformChargeInPercent != null">
              <span class="field-label">Platform Charge</span>
              <span class="field-value">
                {{ appointment.platformChargeInPercent }}%
                <span *ngIf="appointment.platformChargeSource" class="source-tag">{{ appointment.platformChargeSource }}</span>
              </span>
            </div>
          </div>
        </div>
      </ng-container>



      <!-- Flags -->
      <div class="card-section">
        <div class="card-section-title"><i class="fas fa-info-circle"></i> Flags</div>
        <div class="fields-grid">
          <div class="field-row">
            <span class="field-label">Callback Required</span>
            <span class="field-value">
              <span class="flag-badge" [class.flag-yes]="appointment.isCallbackRequired" [class.flag-no]="!appointment.isCallbackRequired">
                {{ appointment.isCallbackRequired ? 'Yes' : 'No' }}
              </span>
            </span>
          </div>
          <div class="field-row">
            <span class="field-label">Agreed</span>
            <span class="field-value">
              <span class="flag-badge" [class.flag-yes]="appointment.isAgreed" [class.flag-no]="!appointment.isAgreed">
                {{ appointment.isAgreed ? 'Yes' : 'No' }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div class="card-divider"></div>

      <!-- Metadata -->
      <div class="card-section">
        <div class="card-section-title"><i class="fas fa-clock"></i> Metadata</div>
        <div class="fields-grid">
          <div class="field-row" *ngIf="appointment.createdAt"><span class="field-label">Created</span><span class="field-value">{{ formatDate(appointment.createdAt) }}</span></div>
          <div class="field-row" *ngIf="appointment.updatedAt"><span class="field-label">Updated</span><span class="field-value">{{ formatDate(appointment.updatedAt) }}</span></div>
          <div class="field-row" *ngIf="appointment.createdBy"><span class="field-label">Created By</span><span class="field-value">{{ appointment.createdBy }}</span></div>
          <div class="field-row" *ngIf="appointment.recurringAppointmentScheduleId">
            <span class="field-label">Recurring ID</span>
            <span class="field-value mono">{{ appointment.recurringAppointmentScheduleId }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- Cancellation Reason -->
    <div class="section-card" *ngIf="appointment.cancellationReason">
      <div class="section-title"><i class="fas fa-ban"></i> Cancellation Reason</div>
      <p class="cancellation-text">{{ appointment.cancellationReason }}</p>
    </div>

    <!-- Scrap Items -->
    <div class="section-card" *ngIf="appointment.scrapItems && appointment.scrapItems.length > 0">
      <div class="section-title"><i class="fas fa-recycle"></i> Scrap Items</div>
      <ul class="category-list">
        <li *ngFor="let item of appointment.scrapItems">{{ item.categoryName || 'N/A' }}</li>
      </ul>
    </div>

    <!-- Disposable Items -->
    <div class="section-card" *ngIf="appointment.disposableItems && appointment.disposableItems.length > 0">
      <div class="section-title"><i class="fas fa-trash-alt"></i> Disposable Items</div>
      <ul class="category-list">
        <li *ngFor="let item of appointment.disposableItems">{{ item.categoryName || 'N/A' }}</li>
      </ul>
    </div>

    <!-- Scrap Preview Images -->
    <div class="section-card" *ngIf="appointment.scrapPreviewImages && appointment.scrapPreviewImages.length > 0">
      <div class="section-title"><i class="fas fa-images"></i> Scrap Preview Images</div>
      <div class="preview-grid">
        <a
          *ngFor="let img of appointment.scrapPreviewImages"
          [href]="img.previewDownloadUrl || '#'"
          target="_blank"
          class="preview-link"
          [title]="img.blobName || 'Preview'">
          <img *ngIf="img.previewDownloadUrl" [src]="img.previewDownloadUrl" [alt]="img.blobName || 'Preview'" class="preview-thumb" />
          <div *ngIf="!img.previewDownloadUrl" class="preview-placeholder">
            <i class="fas fa-image"></i>
            <span>{{ img.blobName }}</span>
          </div>
        </a>
      </div>
    </div>

    <!-- Call Confirmation & Requirements Section Card (Matches Remarks History Style) -->
    <div class="section-card confirmation-card">
      <div class="card-header-flex">
        <div>
          <div class="section-title"><i class="fas fa-tools text-indigo"></i> Call Confirmation & Requirements</div>
          <p class="section-subtitle">Record customer requests during confirmation call so agents pack required tools & choose the right vehicle.</p>
        </div>
        <button class="btn-toggle-req" (click)="toggleRequirementsForm()">
          <i class="fas" [ngClass]="showRequirementsForm ? 'fa-times' : 'fa-edit'"></i>
          {{ showRequirementsForm ? 'Cancel Edit' : (hasSavedRequirements() ? 'Edit Requirements' : '+ Record Requirements') }}
        </button>
      </div>

      <!-- ── SUBMITTED DETAILS SUMMARY VIEW ────────────────────────────── -->
      <div *ngIf="hasSavedRequirements()" class="saved-requirements-summary">
        <div class="summary-fields-grid" *ngIf="appointmentRequirements as req">
          <!-- Tools -->
          <div class="summary-field-row" *ngIf="req.tools && req.tools.length > 0">
            <span class="summary-field-label"><i class="fas fa-tools"></i> Tools Required:</span>
            <div class="summary-badges">
              <span *ngFor="let t of req.tools" class="summary-badge tool-badge">
                <svg *ngIf="t === 'Cutting Machine'" class="custom-tool-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.8 4.2c-.8-.8-2-.8-2.8 0l-5.6 5.6 2.8 2.8 5.6-5.6c.8-.8.8-2 0-2.8z M12 6.5l2 2-1.5 1.5-2-2z M10 11.5l2.5 2.5-1.5 1.5-2.5-2.5z M7.5 13.5c1.7-1.7 4.3-1.7 6 0l-1.4 1.4c-.9-.9-2.3-.9-3.2 0z"/>
                  <circle cx="8.5" cy="15.5" r="4" fill="none" stroke="currentColor" stroke-width="1.3"/>
                  <circle cx="8.5" cy="15.5" r="1" fill="currentColor"/>
                </svg>
                <i *ngIf="t !== 'Cutting Machine'" class="fas" [ngClass]="getToolIconClass(t)"></i>
                {{ t }}
              </span>
            </div>
          </div>

          <!-- Vehicle -->
          <div class="summary-field-row" *ngIf="req.vehicleType">
            <span class="summary-field-label"><i class="fas fa-truck"></i> Vehicle Required:</span>
            <span class="summary-badge vehicle-badge">
              <i class="fas fa-truck-moving"></i> {{ req.vehicleType }}
            </span>
          </div>

          <!-- Extra Workers -->
          <div class="summary-field-row" *ngIf="req.additionalLabourCount != null && req.additionalLabourCount > 0">
            <span class="summary-field-label"><i class="fas fa-users"></i> Extra Workers:</span>
            <span class="summary-badge worker-badge">
              <i class="fas fa-user-plus"></i> +{{ req.additionalLabourCount }} Worker(s)
            </span>
          </div>

          <!-- Heavy Lifting -->
          <div class="summary-field-row" *ngIf="req.heavyLiftingRequired">
            <span class="summary-field-label"><i class="fas fa-dumbbell"></i> Heavy Lifting:</span>
            <span class="summary-badge heavy-badge">
              <i class="fas fa-exclamation-triangle"></i> Required (Heavy Scrap / Machinery)
            </span>
          </div>

          <!-- Customer Special Access Notes -->
          <div class="summary-field-row notes-row" *ngIf="req.specialNotes || getLatestCallNote()">
            <span class="summary-field-label"><i class="fas fa-comment-alt"></i> Access Notes:</span>
            <div class="summary-text-notes">
              "{{ req.specialNotes || getLatestCallNote() }}"
            </div>
          </div>
        </div>
      </div>

      <!-- NO REQUIREMENTS NOTICE -->
      <div *ngIf="!hasSavedRequirements() && !showRequirementsForm" class="no-remarks">
        No call confirmation requirements recorded yet. Click <strong>+ Record Requirements</strong> to log equipment, vehicle size, or access notes.
      </div>

      <!-- ── INTERACTIVE EDIT FORM ─────────────────────────────────────────── -->
      <div *ngIf="showRequirementsForm" class="requirements-form" style="margin-top: 16px;">
        <div class="form-title-bar">
          <i class="fas fa-pen"></i> Select Requirements & Add Remarks
        </div>

        <!-- Tools Required -->
        <div class="form-group">
          <label class="form-label"><i class="fas fa-tools"></i> Tools / Equipment Required</label>
          <div class="chips-container">
            <button
              *ngFor="let tool of availableTools"
              type="button"
              class="chip-item"
              [class.chip-selected]="isToolSelected(tool)"
              (click)="toggleTool(tool)">
              <svg *ngIf="tool === 'Cutting Machine'" class="custom-tool-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.8 4.2c-.8-.8-2-.8-2.8 0l-5.6 5.6 2.8 2.8 5.6-5.6c.8-.8.8-2 0-2.8z M12 6.5l2 2-1.5 1.5-2-2z M10 11.5l2.5 2.5-1.5 1.5-2.5-2.5z M7.5 13.5c1.7-1.7 4.3-1.7 6 0l-1.4 1.4c-.9-.9-2.3-.9-3.2 0z"/>
                <circle cx="8.5" cy="15.5" r="4" fill="none" stroke="currentColor" stroke-width="1.3"/>
                <circle cx="8.5" cy="15.5" r="1" fill="currentColor"/>
              </svg>
              <i *ngIf="tool !== 'Cutting Machine'" class="fas" [ngClass]="getToolIconClass(tool)"></i>
              {{ tool }}
              <i class="fas" [ngClass]="isToolSelected(tool) ? 'fa-check-circle' : 'fa-plus'"></i>
            </button>
          </div>
        </div>

        <!-- Vehicle Type & Labour Count Row -->
        <div class="form-row">
          <div class="form-group flex-1">
            <label class="form-label"><i class="fas fa-truck-moving"></i> Vehicle Required</label>
            <select [(ngModel)]="selectedVehicleType" class="form-select">
              <option value="">-- None / No Vehicle Specified --</option>
              <option *ngFor="let opt of vehicleTypeOptions" [value]="opt">{{ opt }}</option>
            </select>
          </div>

          <div class="form-group flex-1">
            <label class="form-label"><i class="fas fa-users"></i> Additional Workers Needed</label>
            <select [(ngModel)]="additionalLabourCount" class="form-select">
              <option [ngValue]="0">0 (Agent Only)</option>
              <option [ngValue]="1">1 Extra Helper (+1)</option>
              <option [ngValue]="2">2 Extra Workers (+2)</option>
              <option [ngValue]="3">3+ Heavy Team (+3)</option>
            </select>
          </div>
        </div>

        <!-- Heavy Lifting Checkbox -->
        <div class="form-group">
          <label class="checkbox-label">
            <input type="checkbox" [(ngModel)]="heavyLiftingRequired" />
            <span><i class="fas fa-dumbbell"></i> Heavy Lifting Required (Extra heavy scrap / machinery)</span>
          </label>
        </div>

        <!-- Special Access Notes -->
        <div class="form-group">
          <label class="form-label"><i class="fas fa-clipboard-list"></i> Customer Special Remarks & Access Notes</label>
          <textarea
            [(ngModel)]="specialNotes"
            rows="2"
            class="form-textarea"
            placeholder="e.g. 4th floor without lift, scrap kept in basement, customer available after 2 PM..."></textarea>
        </div>

        <!-- Additional Call Note -->
        <div class="form-group">
          <label class="form-label"><i class="fas fa-comment-medical"></i> Add New Call Remark Entry (Appends to history)</label>
          <input
            type="text"
            [(ngModel)]="callRemarkText"
            class="form-input"
            placeholder="Type any additional call conversation note..." />
        </div>

        <!-- Success / Error Banners -->
        <div *ngIf="requirementsSuccessMessage" class="alert-banner alert-success">
          <i class="fas fa-check-circle"></i> {{ requirementsSuccessMessage }}
        </div>
        <div *ngIf="requirementsErrorMessage" class="alert-banner alert-error">
          <i class="fas fa-exclamation-triangle"></i> {{ requirementsErrorMessage }}
        </div>

        <!-- Save Button -->
        <div class="form-actions">
          <button class="btn-save-req" (click)="saveRequirements()" [disabled]="savingRequirements">
            <i *ngIf="savingRequirements" class="fas fa-spinner fa-spin"></i>
            <i *ngIf="!savingRequirements" class="fas fa-save"></i>
            Save Confirmation & Requirements
          </button>
        </div>
      </div>
    </div>

    <!-- Remarks History -->
    <div class="section-card">
      <div class="section-title"><i class="fas fa-comment-dots"></i> Remarks History</div>
      <div *ngIf="!appointment.remarkHistory || appointment.remarkHistory.length === 0" class="no-remarks">
        No remarks found.
      </div>
      <div class="remarks-timeline" *ngIf="appointment.remarkHistory && appointment.remarkHistory.length > 0">
        <div class="remark-entry" *ngFor="let remark of appointment.remarkHistory">
          <div class="remark-header">
            <span class="remark-author">{{ remark.createdBy }}</span>
            <span class="remark-time">{{ remark.createdAt | date:'dd MMM yyyy, h:mm a' }}</span>
          </div>
          <div class="remark-text">{{ remark.text }}</div>
        </div>
      </div>
    </div>

  </div><!-- /detail-content -->


  <!-- ── Remark Popup ──────────────────────────────────────────────────────── -->
  <div *ngIf="showRemarkPopup" class="overlay" (click)="closeRemarkPopup()">
    <div class="sheet" (click)="$event.stopPropagation()">
      <div class="sheet-header">
        <h2>
          <span *ngIf="actionType === 'confirm'">Accept Appointment</span>
          <span *ngIf="actionType === 'reject'">Reject Appointment</span>
          <span *ngIf="actionType === 'cancel'">Cancel Appointment</span>
        </h2>
        <button class="sheet-close" (click)="closeRemarkPopup()" [disabled]="processingAction"><i class="fas fa-times"></i></button>
      </div>
      <div class="sheet-body">
        <p class="sheet-hint">
          <span *ngIf="actionType === 'confirm'">Provide a remark before accepting.</span>
          <span *ngIf="actionType === 'reject'">Provide a reason for rejecting.</span>
          <span *ngIf="actionType === 'cancel'">Provide a reason for cancellation.</span>
        </p>
        <textarea class="sheet-textarea" [(ngModel)]="remarkText" rows="4" placeholder="Type your remark..." [disabled]="processingAction"></textarea>
        <div *ngIf="actionError" class="sheet-error"><i class="fas fa-exclamation-circle"></i> {{ actionError }}</div>
      </div>
      <div class="sheet-footer">
        <button class="btn-secondary" (click)="closeRemarkPopup()" [disabled]="processingAction">Cancel</button>
        <button class="btn-primary" (click)="submitRemark()" [disabled]="!remarkText.trim() || processingAction">
          <i *ngIf="processingAction" class="fas fa-spinner fa-spin"></i>
          <span *ngIf="!processingAction">Submit</span>
        </button>
      </div>
    </div>
  </div>


  <!-- ── Reschedule Sheet ─────────────────────────────────────────────────── -->
  <div *ngIf="showRescheduleModal" class="overlay" (click)="closeRescheduleModal()">
    <div class="sheet" (click)="$event.stopPropagation()">
      <div class="sheet-header">
        <h2>Reschedule Appointment</h2>
        <button class="sheet-close" (click)="closeRescheduleModal()" [disabled]="processingReschedule"><i class="fas fa-times"></i></button>
      </div>
      <div class="sheet-body">
        <div class="form-group">
          <label>New Date *</label>
          <input type="date" [(ngModel)]="rescheduleDate" [min]="getCurrentDate()" class="form-input" [disabled]="processingReschedule" />
        </div>
        <div class="form-group">
          <label>New Time Slot *</label>
          <select [(ngModel)]="rescheduleTimeSlot" class="form-select" [disabled]="processingReschedule">
            <option value="">Select a time slot</option>
            <option *ngFor="let slot of timeSlotOptions" [value]="slot">{{ slot }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>Remarks</label>
          <textarea [(ngModel)]="rescheduleRemarks" rows="3" class="sheet-textarea" placeholder="Optional remarks..." [disabled]="processingReschedule"></textarea>
        </div>
        <div *ngIf="rescheduleError" class="sheet-error"><i class="fas fa-exclamation-circle"></i> {{ rescheduleError }}</div>
      </div>
      <div class="sheet-footer">
        <button class="btn-secondary" (click)="closeRescheduleModal()" [disabled]="processingReschedule">Cancel</button>
        <button class="btn-primary" (click)="submitReschedule()" [disabled]="!rescheduleDate || !rescheduleTimeSlot || processingReschedule">
          <i *ngIf="processingReschedule" class="fas fa-spinner fa-spin"></i>
          <span *ngIf="!processingReschedule"><i class="fas fa-calendar-check"></i> Reschedule</span>
        </button>
      </div>
    </div>
  </div>


  <!-- ── Assign Agent Sheet ───────────────────────────────────────────────── -->
  <div *ngIf="showAssignAgentModal" class="overlay" (click)="closeAssignAgentModal()">
    <div class="sheet" (click)="$event.stopPropagation()">
      <div class="sheet-header">
        <h2>Assign Agent</h2>
        <button class="sheet-close" (click)="closeAssignAgentModal()" [disabled]="assigningAgent"><i class="fas fa-times"></i></button>
      </div>
      <div class="sheet-body">
        <div *ngIf="loadingAgents" class="loading-inline"><i class="fas fa-spinner fa-spin"></i> Loading agents...</div>
        <div class="form-group" *ngIf="!loadingAgents">
          <label>Select Agent *</label>
          <select [(ngModel)]="selectedAgentId" class="form-select" [disabled]="assigningAgent">
            <option value="">-- Select an agent --</option>
            <option *ngFor="let agent of agents" [value]="agent.agentId">{{ agent.name }}</option>
          </select>
        </div>
        <div *ngIf="assignAgentError" class="sheet-error"><i class="fas fa-exclamation-circle"></i> {{ assignAgentError }}</div>
      </div>
      <div class="sheet-footer">
        <button class="btn-secondary" (click)="closeAssignAgentModal()" [disabled]="assigningAgent">Cancel</button>
        <button class="btn-primary" (click)="assignAgent()" [disabled]="!selectedAgentId || assigningAgent">
          <i *ngIf="assigningAgent" class="fas fa-spinner fa-spin"></i>
          <span *ngIf="!assigningAgent"><i class="fas fa-user-check"></i> Assign</span>
        </button>
      </div>
    </div>
  </div>


  <!-- ── Update Fees Modal ───────────────────────────────────────────────── -->
  <app-update-appointment-fees
    *ngIf="showUpdateFeesModal"
    [appointment]="appointment"
    [showModal]="showUpdateFeesModal"
    (feesUpdated)="onFeesUpdated()"
    (closeModal)="onCloseFeesModal()">
  </app-update-appointment-fees>

</div>
